import React, { useContext, useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import useApi from "../hooks/useApi";
import { AuthContext } from '../context/AuthContext';
import ForgotPasswordModal from './ForgotPasswordModal';

const VendorLoginModal = ({ show, onClose, isRegister = false }) => {
  const businessTypeOptions = [
    'Car Wash',
    'Mechanic',
    'Tyre Service',
    'Car Detailing',
    'Battery Service',
    'Car Accessories',
  ];

  const navigate = useNavigate();
  const { publicAuth } = useApi();
  const { loginUser, saveAuthToken, setViewMode } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState(isRegister ? 'register' : 'login');
  const [loginMethod, setLoginMethod] = useState('email');
  const [showForgot, setShowForgot] = useState(false);
  const [registerStep, setRegisterStep] = useState('details');
  const [registerOtpEmail, setRegisterOtpEmail] = useState('');
  const [registerOtp, setRegisterOtp] = useState('');
  const [registerOtpCountdown, setRegisterOtpCountdown] = useState(0);
  const [registrationNotice, setRegistrationNotice] = useState('');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [googleMapLink, setGoogleMapLink] = useState('');
  const [bankDetails, setBankDetails] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [vendorStatus, setVendorStatus] = useState('0');
  const [photoFile, setPhotoFile] = useState(null);
  const [idProofFile, setIdProofFile] = useState(null);
  const [licenseProofFile, setLicenseProofFile] = useState(null);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [registerErrors, setRegisterErrors] = useState({});
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setActiveTab(isRegister ? 'register' : 'login');
    }
  }, [show, isRegister]);

  useEffect(() => {
    if (registerOtpCountdown > 0) {
      const timer = setTimeout(() => setRegisterOtpCountdown(registerOtpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [registerOtpCountdown]);

  const resetForms = () => {
    setActiveTab(isRegister ? 'register' : 'login');
    setLoginMethod('email');
    setShowForgot(false);

    setLoginEmail('');
    setLoginPhone('');
    setLoginPassword('');
    setShowLoginPassword(false);
    setLoginErrors({});
    setIsLoginLoading(false);
    setRegisterStep('details');
    setRegisterOtpEmail('');
    setRegisterOtp('');
    setRegisterOtpCountdown(0);
    setRegistrationNotice('');

    setName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setBusinessName('');
    setBusinessType('');
    setLicenseNumber('');
    setExperienceYears('');
    setAddress('');
    setCity('');
    setState('');
    setZipcode('');
    setGoogleMapLink('');
    setBankDetails('');
    setGstNumber('');
    setVendorStatus('0');
    setPhotoFile(null);
    setIdProofFile(null);
    setLicenseProofFile(null);
    setShowRegisterPassword(false);
    setRegisterErrors({});
    setIsRegisterLoading(false);
  };

  const handleClose = () => {
    resetForms();
    onClose?.();
  };

  const modalTitle = activeTab === 'register'
    ? (registerStep === 'verify' ? 'Verify Vendor Email' : 'Vendor Registration')
    : 'Vendor Login';

  const validateLogin = () => {
    const next = {};

    if (loginMethod === 'email') {
      if (!loginEmail.trim()) next.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
        next.email = 'Enter a valid email';
      }
    } else {
      if (!loginPhone.trim()) next.phone = 'Phone is required';
      else if (!/^\d{10}$/.test(loginPhone)) {
        next.phone = 'Enter 10-digit phone number';
      }
    }

    if (!loginPassword.trim()) next.password = 'Password is required';
    else if (loginPassword.length < 6) next.password = 'Minimum 6 characters';

    setLoginErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateRegister = () => {
    const next = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!email.trim()) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Enter a valid email';
    }
    if (!phone.trim()) next.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(phone)) {
      next.phone = 'Enter 10-digit phone number';
    }
    if (!password.trim()) next.password = 'Password is required';
    else if (password.length < 6) next.password = 'Minimum 6 characters';
    if (!businessType.trim()) next.businessType = 'Business type is required';
    if (!experienceYears.trim()) next.experienceYears = 'Experience is required';
    else if (Number.isNaN(Number(experienceYears)) || Number(experienceYears) < 0) {
      next.experienceYears = 'Enter a valid experience value';
    }
    if (!address.trim()) next.address = 'Address is required';
    if (!city.trim()) next.city = 'City is required';
    if (!state.trim()) next.state = 'State is required';
    if (!zipcode.trim()) next.zipcode = 'Zip code is required';

    setRegisterErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateRegisterOtp = () => {
    if (!registerOtpEmail.trim()) return false;
    return /^\d{6}$/.test(registerOtp);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setIsLoginLoading(true);
    try {
      const payload = {
        [loginMethod === 'email' ? 'email' : 'phone']: loginMethod === 'email' ? loginEmail : loginPhone,
        password: loginPassword,
      };

      const response = await publicAuth("vendor/login", "POST", payload);

      if (response.data?.status === true) {
        const vendor = response.data?.data || null;
        loginUser(vendor, response.data?.token, "vendor");
        setViewMode("vendor");
        saveAuthToken(response.data?.token);
        navigate('/vendor/dashboard');
        handleClose();
      } else {
        throw new Error(response.data?.message || "Invalid credentials");
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: error.response?.data?.message || error.message || 'Something went wrong',
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegister()) return;

    setIsRegisterLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', name);
      payload.append('email', email);
      payload.append('phone', phone);
      payload.append('password', password);
      payload.append('business_name', businessName);
      payload.append('business_type', businessType);
      payload.append('license_number', licenseNumber);
      payload.append('experience_years', experienceYears);
      payload.append('address', address);
      payload.append('city', city);
      payload.append('state', state);
      payload.append('zipcode', zipcode);
      payload.append('google_map_link', googleMapLink);
      payload.append('bank_details', bankDetails);
      payload.append('gst_number', gstNumber);
      payload.append('status', '0');

      if (photoFile) payload.append('photo', photoFile);
      if (idProofFile) payload.append('id_proof', idProofFile);
      if (licenseProofFile) payload.append('license_proof', licenseProofFile);

      const response = await publicAuth("vendor/register", "POST", payload);
      if (response.data?.status === true) {
        setRegisterOtpEmail(email);
        setRegisterOtp('');
        setRegisterStep('verify');
        setRegisterOtpCountdown(60);
        setRegistrationNotice(
          'Registration successful. Please verify the OTP sent to your email. After verification, your account will wait for admin approval.'
        );
        Swal.fire({
          icon: 'success',
          title: 'Registration Submitted',
          text: 'Please verify the OTP sent to your email. After verification, your vendor account will wait for admin approval.',
        });
      } else {
        throw new Error(response.data?.message || 'Registration failed');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: error.response?.data?.message || error.message || 'Something went wrong',
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const handleVerifyRegisterOtp = async (e) => {
    e.preventDefault();
    if (!validateRegisterOtp()) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid OTP',
        text: 'Please enter the 6-digit OTP sent to your email.',
      });
      return;
    }

    try {
      setIsRegisterLoading(true);
      const response = await publicAuth('vendor/verify-email-otp', 'POST', {
        email: registerOtpEmail,
        otp: registerOtp,
      });

      if (response.data?.status === true) {
        Swal.fire({
          icon: 'success',
          title: 'Email Verified',
          text: 'Your email is verified. Your account is waiting for admin approval. You can log in once approved.',
        });
        setLoginMethod('email');
        setLoginEmail(registerOtpEmail);
        setActiveTab('login');
        setRegistrationNotice('Your email is verified. Your account is waiting for admin approval.');
        setRegisterStep('details');
        setRegisterOtp('');
        setRegisterOtpEmail('');
        setRegisterOtpCountdown(0);
      } else {
        throw new Error(response.data?.message || 'OTP verification failed');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: error.response?.data?.message || error.message || 'Something went wrong',
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const handleResendRegisterOtp = async () => {
    if (!registerOtpEmail || registerOtpCountdown > 0) return;

    try {
      setIsRegisterLoading(true);
      const response = await publicAuth('vendor/resend-email-otp', 'POST', {
        email: registerOtpEmail,
      });

      if (response.data?.status === true) {
        setRegisterOtpCountdown(60);
        Swal.fire({
          icon: 'success',
          title: 'OTP Sent',
          text: 'A new OTP has been sent to your email.',
        });
      } else {
        throw new Error(response.data?.message || 'Unable to resend OTP');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Resend Failed',
        text: error.response?.data?.message || error.message || 'Something went wrong',
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  return (
    <>
      <ForgotPasswordModal
        show={showForgot}
        handleClose={() => setShowForgot(false)}
        userType="vendor"
      />

      <Modal
        show={show}
        onHide={handleClose}
        centered
        size={activeTab === 'register' ? 'xl' : 'md'}
        scrollable
        style={showForgot ? { pointerEvents: 'none', opacity: 0.5 } : {}}
      >
        <Modal.Header
          closeButton
          className="border-0 pb-0"
          style={showForgot ? { pointerEvents: 'none' } : {}}
        >
          <Modal.Title className="w-100 text-center">
            <h4>{modalTitle}</h4>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={showForgot ? { pointerEvents: 'none' } : {}}>
          {activeTab === 'login' ? (
            <Form onSubmit={handleLogin}>
              <div className="mb-3">
                <div className="btn-group w-100" role="group" aria-label="Login method">
                  <Button
                    type="button"
                    variant={loginMethod === 'email' ? 'primary' : 'outline-primary'}
                    onClick={() => !showForgot && setLoginMethod('email')}
                    disabled={showForgot}
                  >
                    Email Login
                  </Button>
                  <Button
                    type="button"
                    variant={loginMethod === 'phone' ? 'primary' : 'outline-primary'}
                    onClick={() => !showForgot && setLoginMethod('phone')}
                    disabled={showForgot}
                  >
                    Phone Login
                  </Button>
                </div>
              </div>

              {loginMethod === 'email' ? (
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={loginEmail}
                    onChange={(e) => {
                      if (!showForgot) {
                        setLoginEmail(e.target.value);
                        setLoginErrors((prev) => ({ ...prev, email: '' }));
                      }
                    }}
                    isInvalid={!!loginErrors.email}
                    disabled={showForgot}
                    readOnly={showForgot}
                  />
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter your phone number"
                    value={loginPhone}
                    onChange={(e) => {
                      if (!showForgot) {
                        setLoginPhone(e.target.value);
                        setLoginErrors((prev) => ({ ...prev, phone: '' }));
                      }
                    }}
                    isInvalid={!!loginErrors.phone}
                    disabled={showForgot}
                    readOnly={showForgot}
                  />
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showLoginPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => {
                      if (!showForgot) {
                        setLoginPassword(e.target.value);
                        setLoginErrors((prev) => ({ ...prev, password: '' }));
                      }
                    }}
                    isInvalid={!!loginErrors.password}
                    disabled={showForgot}
                    readOnly={showForgot}
                  />
                  <Button
                    variant="link"
                    className="position-absolute end-0 top-0 text-decoration-none"
                    onClick={() => !showForgot && setShowLoginPassword(!showLoginPassword)}
                    disabled={showForgot}
                  >
                    {showLoginPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.password}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>

                <div className="d-grid mb-3">
                  <Button variant="primary" type="submit" size="lg" disabled={isLoginLoading || showForgot}>
                    {isLoginLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
                </div>

                <div className="text-center">
                  <Button
                    variant="link"
                  className="p-0 mt-2 text-decoration-none"
                  onClick={() => setShowForgot(true)}
                  disabled={showForgot}
                  >
                    Forgot Password?
                  </Button>
                </div>

                <div className="text-center mt-2">
                  <span className="text-muted">Don't have an account? </span>
                <Button
                  variant="link"
                  className="p-0 align-baseline text-decoration-none"
                  onClick={() => {
                    if (showForgot) return;
                    setActiveTab('register');
                    setRegisterStep('details');
                    setRegistrationNotice('');
                  }}
                  disabled={showForgot}
                >
                  Register
                  </Button>
                </div>
              </Form>
          ) : registerStep === 'details' ? (
            <Form onSubmit={handleRegister} key={`vendor-register-${show ? 'open' : 'closed'}`}>
              {registrationNotice ? (
                <div className="alert alert-info">{registrationNotice}</div>
              ) : null}
              <div className="mb-3">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="fw-semibold border-bottom pb-2 mb-2">Basic Information</div>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Vendor Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" placeholder="Enter vendor name" value={name} onChange={(e) => setName(e.target.value)} isInvalid={!!registerErrors.name} />
                      <Form.Control.Feedback type="invalid">{registerErrors.name}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="email" placeholder="Enter email" value={email} onChange={(e) => setEmail(e.target.value)} isInvalid={!!registerErrors.email} />
                      <Form.Control.Feedback type="invalid">{registerErrors.email}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="tel" placeholder="Enter phone number" value={phone} onChange={(e) => setPhone(e.target.value)} isInvalid={!!registerErrors.phone} />
                      <Form.Control.Feedback type="invalid">{registerErrors.phone}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type={showRegisterPassword ? 'text' : 'password'}
                          placeholder="Create password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isInvalid={!!registerErrors.password}
                        />
                        <Button
                          variant="link"
                          className="position-absolute end-0 top-0 text-decoration-none"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          type="button"
                        >
                          {showRegisterPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                        <Form.Control.Feedback type="invalid">{registerErrors.password}</Form.Control.Feedback>
                      </div>
                    </Form.Group>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold border-bottom pb-2 mb-2">Business Details</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Business Name</Form.Label>
                      <Form.Control type="text" placeholder="Enter business name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Business Type <span className="text-danger">*</span></Form.Label>
                      <Form.Select value={businessType} onChange={(e) => setBusinessType(e.target.value)} required isInvalid={!!registerErrors.businessType}>
                        <option value="">Select Business Type</option>
                        {businessTypeOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">{registerErrors.businessType}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>License Number</Form.Label>
                      <Form.Control type="text" placeholder="Enter license number" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Experience (Years) <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        placeholder="Enter experience"
                        value={experienceYears}
                        onChange={(e) => setExperienceYears(e.target.value)}
                        required
                        isInvalid={!!registerErrors.experienceYears}
                      />
                      <Form.Control.Feedback type="invalid">{registerErrors.experienceYears}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold border-bottom pb-2 mb-2">Location Details</div>
                <div className="row g-3">
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        required
                        isInvalid={!!registerErrors.address}
                      />
                      <Form.Control.Feedback type="invalid">{registerErrors.address}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>City <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        isInvalid={!!registerErrors.city}
                      />
                      <Form.Control.Feedback type="invalid">{registerErrors.city}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>State <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter state"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        required
                        isInvalid={!!registerErrors.state}
                      />
                      <Form.Control.Feedback type="invalid">{registerErrors.state}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Zip Code <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter zip code"
                        value={zipcode}
                        onChange={(e) => setZipcode(e.target.value)}
                        required
                        isInvalid={!!registerErrors.zipcode}
                      />
                      <Form.Control.Feedback type="invalid">{registerErrors.zipcode}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label>Google Map Link</Form.Label>
                      <Form.Control type="url" placeholder="https://maps.google.com/..." value={googleMapLink} onChange={(e) => setGoogleMapLink(e.target.value)} />
                    </Form.Group>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold border-bottom pb-2 mb-2">Documents Upload</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Photo / Logo</Form.Label>
                      <Form.Control type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>ID Proof</Form.Label>
                      <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setIdProofFile(e.target.files?.[0] || null)} />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>License Proof</Form.Label>
                      <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setLicenseProofFile(e.target.files?.[0] || null)} />
                    </Form.Group>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold border-bottom pb-2 mb-2">Account Settings</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Bank Details</Form.Label>
                      <Form.Control as="textarea" rows={2} placeholder="Enter bank details" value={bankDetails} onChange={(e) => setBankDetails(e.target.value)} />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>GST Number</Form.Label>
                      <Form.Control type="text" placeholder="Enter GST number" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select value={vendorStatus} disabled>
                        <option value="0">Pending Approval</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Vendors can log in after email verification and admin approval.</Form.Text>
                    </Form.Group>
                  </div>
                </div>
              </div>

              <div className="d-grid">
                <Button variant="primary" type="submit" size="lg" disabled={isRegisterLoading}>
                  {isRegisterLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Registering...
                    </>
                  ) : (
                    'Register Vendor'
                  )}
                </Button>
              </div>

              <div className="text-center mt-3">
                <span className="text-muted">Already have an account? </span>
                <Button
                  variant="link"
                  className="p-0 align-baseline text-decoration-none"
                  onClick={() => {
                    if (showForgot) return;
                    setActiveTab('login');
                    setRegistrationNotice('');
                  }}
                  disabled={showForgot}
                  type="button"
                >
                  Login
                </Button>
              </div>
            </Form>
          ) : (
            <Form onSubmit={handleVerifyRegisterOtp}>
              <Alert variant="info">
                We sent a 6-digit OTP to <strong>{registerOtpEmail}</strong>. Verify it first, then wait for admin approval before logging in.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>OTP</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  value={registerOtp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setRegisterOtp(value);
                  }}
                  required
                />
              </Form.Group>

              <div className="d-flex justify-content-between align-items-center mb-4">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={handleResendRegisterOtp}
                  disabled={isRegisterLoading || registerOtpCountdown > 0}
                  type="button"
                >
                  {registerOtpCountdown > 0 ? `Resend OTP in ${registerOtpCountdown}s` : 'Resend OTP'}
                </Button>

                <Button
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={() => {
                    setRegisterStep('details');
                    setRegistrationNotice('');
                  }}
                  type="button"
                >
                  Edit registration
                </Button>
              </div>

              <div className="d-grid">
                <Button variant="primary" type="submit" size="lg" disabled={isRegisterLoading || !validateRegisterOtp()}>
                  {isRegisterLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Email'
                  )}
                </Button>
              </div>
            </Form>
            )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default VendorLoginModal;
